<template>
  <article
    class="draft-card"
    :class="{ 'draft-card-ready': draft.isReady }"
  >
    <!-- ======================================================
         IMAGE
    ======================================================= -->

    <div class="draft-image-wrapper">
      <img
        v-if="draft.imageUrl"
        :src="draft.imageUrl"
        :alt="draft.title || 'Вакансия'"
        class="draft-image"
      />

      <div
        v-else
        class="draft-image-placeholder"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <rect
            x="3"
            y="4"
            width="18"
            height="16"
            rx="3"
            stroke="currentColor"
            stroke-width="1.5"
          />

          <circle
            cx="9"
            cy="10"
            r="2"
            stroke="currentColor"
            stroke-width="1.5"
          />

          <path
            d="M5.5 17 10 13l3 2.5 2.5-2L19 17"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        <span>Нет изображения</span>
      </div>

      <div
        class="ready-badge"
        :class="{ ready: draft.isReady }"
      >
        {{ draft.isReady ? "Готово" : "В обработке" }}
      </div>
    </div>

    <!-- ======================================================
         BODY
    ======================================================= -->

    <div class="draft-body">
      <!-- ====================================================
           HEADER
      ===================================================== -->

      <div class="draft-header">
        <div class="draft-title-block">
          <div class="source-row">
            <span class="source-badge">
              {{ sourceTitle }}
            </span>

            <span
              v-if="draft.externalId"
              class="external-id"
            >
              ID {{ draft.externalId }}
            </span>
          </div>

          <div class="draft-title-editor">

            <template v-if="!isEditingTitle">

              <h3
                class="draft-title"
                @dblclick="startTitleEditing"
              >
                {{ titleText || "Без названия" }}
              </h3>

              <button
                type="button"
                class="edit-title-button"
                title="Изменить название"
                @click="startTitleEditing"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M14 5L19 10"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                  />

                  <path
                    d="M5 19L7 13L16 4L20 8L11 17L5 19Z"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>

            </template>


            <template v-else>

              <div class="draft-title-edit-row">

                <input
                  ref="titleInput"
                  v-model="editableTitle"
                  class="draft-title-input"
                  type="text"
                  maxlength="500"
                  placeholder="Название вакансии"
                  @keydown.enter.prevent="onUpdateTitle"
                  @keydown.esc.prevent="cancelTitleEditing"
                />

                <button
                  type="button"
                  class="title-save-button"
                  :disabled="!isTitleChanged"
                  title="Сохранить"
                  @click="onUpdateTitle"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 12.5L10 17L19 7"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>

                <button
                  type="button"
                  class="title-cancel-button"
                  title="Отменить"
                  @click="cancelTitleEditing"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M6 6L18 18"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                    />

                    <path
                      d="M18 6L6 18"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>

              </div>

            </template>

            </div>

          <div class="company-line">
            <span class="company-prefix">
              Компания
            </span>

            <span class="company-name">
              {{ draft.companyName || "Не определена" }}
            </span>
          </div>
        </div>

        <div class="header-actions">

          <a
            v-if="companyStorageUrl"
            :href="companyStorageUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="storage-folder-link"
            title="Открыть изображения компании в Firebase Storage"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3.5 6.5C3.5 5.7 4.2 5 5 5H9L11 7H19C19.8 7 20.5 7.7 20.5 8.5V18C20.5 18.8 19.8 19.5 19 19.5H5C4.2 19.5 3.5 18.8 3.5 18V6.5Z"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linejoin="round"
              />

              <path
                d="M3.5 9H20.5"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />

              <path
                d="M12 12V17"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />

              <path
                d="M9.5 14.5H14.5"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />
            </svg>
          </a>

          <a
            v-if="draft.sourceUrl"
            :href="draft.sourceUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="source-link"
            title="Открыть источник"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M14 5h5v5"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />

              <path
                d="m19 5-8 8"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />

              <path
                d="M18 13v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />
            </svg>
          </a>

          <button
            type="button"
            class="delete-draft-button"
            title="Удалить черновик"
            @click="onDeleteDraft"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 7H20"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />

              <path
                d="M9 3H15L16 7H8L9 3Z"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linejoin="round"
              />

              <path
                d="M7 7L8 20H16L17 7"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linejoin="round"
              />

              <path
                d="M10 11V16"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />

              <path
                d="M14 11V16"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- ====================================================
           META
      ===================================================== -->

      <div class="meta-row">
        <span
          v-if="draft.city"
          class="meta-item"
        >
          {{ draft.city }}
        </span>

        <span
          v-if="salaryText"
          class="meta-item"
        >
          {{ salaryText }}
        </span>

        <span
          v-if="draft.level"
          class="meta-item"
        >
          {{ draft.level }}
        </span>

        <span
          v-if="draft.employmentType"
          class="meta-item"
        >
          {{ draft.employmentType }}
        </span>
      </div>

      <!-- ====================================================
          DESCRIPTION
      ===================================================== -->

      <div
        v-if="descriptionText"
        class="description-block"
      >
        <div class="description-header">
          <span>Описание вакансии</span>

          <span class="description-length">
            {{ editableDescription.length }} символов
          </span>
        </div>

        <textarea
          v-model="editableDescription"
          class="draft-description-input"
          rows="10"
          placeholder="Описание вакансии"
        />

        <div class="description-actions">
          <button
            type="button"
            class="update-description-button"
            :disabled="!isDescriptionChanged || isUpdatingDescription"
            @click="onUpdateDescription"
          >
            <span v-if="!isUpdatingDescription">
              Обновить
            </span>

            <span v-else>
              Обновляем...
            </span>
          </button>
        </div>
</div>

      <!-- ====================================================
           CONTACTS
      ===================================================== -->

      <div
        v-if="hasContacts"
        class="contacts-block"
      >
        <div class="contacts-header">
          <span class="contacts-label">
            Контакты
          </span>

          <span class="contacts-hint">
            Отклик на вакансию
          </span>
        </div>

        <div class="contacts-list">
          <a
            v-if="contactTelegram"
            :href="telegramUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="contact-chip telegram"
            :title="contactTelegram"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M21 4L3 11L10 13.5L12.5 20L21 4Z"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linejoin="round"
              />

              <path
                d="M10 13.5L15 9"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />
            </svg>

            <span>Telegram</span>
          </a>

          <a
            v-if="contactEmail"
            :href="emailUrl"
            class="contact-chip email"
            :title="contactEmail"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <rect
                x="3"
                y="5"
                width="18"
                height="14"
                rx="2"
                stroke="currentColor"
                stroke-width="1.7"
              />

              <path
                d="M4 7L12 13L20 7"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linejoin="round"
              />
            </svg>

            <span>{{ contactEmail }}</span>
          </a>

          <a
            v-if="contactApplyUrl"
            :href="contactApplyUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="contact-chip apply"
            title="Открыть ссылку для отклика"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M14 5H19V10"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />

              <path
                d="M19 5L10 14"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />

              <path
                d="M18 13V18C18 19.1 17.1 20 16 20H6C4.9 20 4 19.1 4 18V8C4 6.9 4.9 6 6 6H11"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />
            </svg>

            <span>Отклик</span>
          </a>
        </div>
      </div>

      <!-- ====================================================
           RESOLUTION STATUS
      ===================================================== -->

      <div class="resolution-grid">
        <!-- COMPANY -->

        <div class="resolution-item">
          <div class="resolution-label">
            Компания
          </div>

          <div
            class="status-pill"
            :class="statusClass(draft.companyResolutionStatus)"
          >
            <span class="status-dot"></span>
            {{ companyStatusTitle }}
          </div>

          <div
            v-if="draft.isCompanyResolved && draft.companyMatchType"
            class="resolution-info"
          >
            {{ draft.companyMatchType }}

            <template v-if="draft.companyMatchScore !== null">
              · {{ draft.companyMatchScore }}%
            </template>
          </div>

          <div
            v-else-if="draft.companyResolutionReason"
            class="resolution-error"
          >
            {{ draft.companyResolutionReason }}
          </div>
        </div>

        <!-- IMAGE -->

        <div class="resolution-item">
          <div class="resolution-label">
            Изображение
          </div>

          <div
            class="status-pill"
            :class="statusClass(draft.imageResolutionStatus)"
          >
            <span class="status-dot"></span>
            {{ imageStatusTitle }}
          </div>

          <div
            v-if="draft.imageStoragePath"
            class="resolution-info storage-path"
          >
            {{ draft.imageStoragePath }}
          </div>
        </div>
      </div>

      <!-- ====================================================
           ACTIONS
      ===================================================== -->

      <div
        v-if="
          draft.isReady
          || !draft.isCompanyResolved
          || !draft.companyId
          || !draft.imageUrl
        "
        class="draft-actions"
      >
        <!-- ==================================================
             READY
        =================================================== -->

        <button
          v-if="draft.isReady"
          type="button"
          class="draft-action-button publish"
          @click="onPublish"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 19V5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />

            <path
              d="M7 10L12 5L17 10"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />

            <path
              d="M5 15V19C5 20.1 5.9 21 7 21H17C18.1 21 19 20.1 19 19V15"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>

          <span>Публиковать</span>
        </button>

        <!-- ==================================================
             COMPANY NOT FOUND
        =================================================== -->

        <button
          v-else-if="
            !draft.isCompanyResolved
            || !draft.companyId
          "
          type="button"
          class="draft-action-button company"
          title="Создать компанию"
          @click="onCreateCompany"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M4 20V8L12 4L20 8V20"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
              stroke-linejoin="round"
            />

            <path
              d="M9 20V14H15V20"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
              stroke-linejoin="round"
            />

            <path
              d="M12 7V11"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />

            <path
              d="M10 9H14"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />
          </svg>

          <span>Создать компанию</span>
        </button>

        <!-- ==================================================
             IMAGE NOT FOUND
        =================================================== -->

        <button
          v-else-if="
            draft.isCompanyResolved
            && draft.companyId
            && !draft.imageUrl
          "
          type="button"
          class="draft-action-button image"
          title="Добавить изображения компании"
          @click="onAddImages"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <rect
              x="3"
              y="4"
              width="18"
              height="16"
              rx="3"
              stroke="currentColor"
              stroke-width="1.7"
            />

            <circle
              cx="8.5"
              cy="9"
              r="1.7"
              stroke="currentColor"
              stroke-width="1.7"
            />

            <path
              d="M5.5 17L10 12.5L13 15.5L15.5 13L18.5 16"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
              stroke-linejoin="round"
            />

            <path
              d="M18 2.5V7.5"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />

            <path
              d="M15.5 5H20.5"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />
          </svg>

          <span>Добавить картинки</span>
        </button>
      </div>

      <!-- ====================================================
           FOOTER
      ===================================================== -->

      <div class="draft-footer">
        <span class="draft-id">
          {{ draft.id }}
        </span>

        <span
          v-if="formattedUpdatedAt"
          class="updated-at"
        >
          {{ formattedUpdatedAt }}
        </span>
      </div>
    </div>
  </article>
</template>

<script setup>

import {
  computed,
  nextTick,
  ref,
  watch
} from "vue";


const props = defineProps({
  draft: {
    type: Object,
    required: true
  }
});

const emit = defineEmits([
  "create-company",
  "add-images",
  "publish",
  "delete-draft",
  "update-description",
  "update-title"
]);

// ========================================================
// TITLE
// ========================================================

const titleText = computed(() => {
  return String(
    props.draft.title
    || props.draft.vacancy?.title
    || ""
  );
});


const originalTitle = ref("");
const editableTitle = ref("");
const isEditingTitle = ref(false);
const titleInput = ref(null);


function syncTitle(value) {
  const title = String(
    value || ""
  );

  originalTitle.value =
    title;

  editableTitle.value =
    title;
}


watch(
  titleText,
  value => {
    syncTitle(value);
  },
  {
    immediate: true
  }
);


const isTitleChanged = computed(() => {
  return (
    editableTitle.value.trim()
    !== originalTitle.value.trim()
  );
});


async function startTitleEditing() {
  editableTitle.value =
    originalTitle.value;

  isEditingTitle.value =
    true;

  await nextTick();

  titleInput.value?.focus();
  titleInput.value?.select();
}


function cancelTitleEditing() {
  editableTitle.value =
    originalTitle.value;

  isEditingTitle.value =
    false;
}


function onUpdateTitle() {
  const title =
    editableTitle.value.trim();

  if (!title) {
    window.alert(
      "Название вакансии не может быть пустым"
    );

    return;
  }

  if (!isTitleChanged.value) {
    isEditingTitle.value =
      false;

    return;
  }

  emit(
    "update-title",
    {
      draft: props.draft,
      title
    }
  );

  isEditingTitle.value =
    false;
}


// ========================================================
// DESCRIPTION
// ========================================================

const descriptionText = computed(() => {
  return String(
    props.draft.description
    || props.draft.vacancy?.description
    || ""
  );
});


const originalDescription = ref("");
const editableDescription = ref("");
const isUpdatingDescription = ref(false);


function syncDescription(value) {
  const description = String(
    value || ""
  );

  originalDescription.value =
    description;

  editableDescription.value =
    description;
}


/*
 * Следим именно за description с backend.
 *
 * Изменение textarea НЕ меняет descriptionText,
 * поэтому пользователь спокойно редактирует текст,
 * а originalDescription остаётся исходным значением.
 *
 * Если backend потом пришлёт обновлённый description,
 * локальное состояние синхронизируется.
 */
watch(
  descriptionText,
  value => {
    syncDescription(value);
  },
  {
    immediate: true
  }
);


const isDescriptionChanged = computed(() => {
  return (
    editableDescription.value
    !== originalDescription.value
  );
});


function onUpdateDescription() {
  const description =
    editableDescription.value.trim();

  if (!description) {
    window.alert(
      "Описание вакансии не может быть пустым"
    );

    return;
  }

  if (!isDescriptionChanged.value) {
    return;
  }

  emit(
    "update-description",
    {
      draft: props.draft,
      description
    }
  );
}


// ========================================================
// CONTACTS
// ========================================================

const contacts = computed(() => {
  return props.draft.contacts
    || props.draft.vacancy?.contacts
    || {};
});


const contactEmail = computed(() => {
  const value =
    contacts.value.email;

  if (!value) {
    return null;
  }

  return String(value)
    .replace(/^mailto:/i, "")
    .trim();
});


const contactTelegram = computed(() => {
  const value =
    contacts.value.telegram;

  if (!value) {
    return null;
  }

  return String(value).trim();
});


const contactApplyUrl = computed(() => {
  return contacts.value.apply_url
    || contacts.value.applyUrl
    || null;
});


const hasContacts = computed(() => {
  return Boolean(
    contactTelegram.value
    || contactEmail.value
    || contactApplyUrl.value
  );
});


const telegramUrl = computed(() => {
  const value =
    contactTelegram.value;

  if (!value) {
    return null;
  }

  if (
    value.startsWith("http://")
    || value.startsWith("https://")
    || value.startsWith("tg://")
  ) {
    return value;
  }

  const username = value
    .replace(/^@/, "")
    .replace(/^t\.me\//, "")
    .replace(/^telegram\.me\//, "");

  return `https://t.me/${username}`;
});


const emailUrl = computed(() => {
  if (!contactEmail.value) {
    return null;
  }

  return `mailto:${contactEmail.value}`;
});

// ========================================================
// FIREBASE STORAGE
// ========================================================

const companyStorageUrl = computed(() => {
  const companyId =
    props.draft.companyId;

  if (!companyId) {
    return null;
  }

  const encodedCompanyId =
    encodeURIComponent(
      String(companyId)
    );

  return (
    "https://console.firebase.google.com/u/0/project/"
    + "hyper-e122e/storage/"
    + "hyper-e122e.appspot.com/files/"
    + "~2FCompaniesPictures~2F"
    + encodedCompanyId
  );
});

// ========================================================
// ACTIONS
// ========================================================

function onCreateCompany() {
  emit(
    "create-company",
    props.draft
  );
}


function onAddImages() {
  if (!props.draft.companyId) {
    return;
  }

  emit(
    "add-images",
    props.draft
  );
}


function onPublish() {
  emit(
    "publish",
    props.draft
  );
}


function onDeleteDraft() {
  const confirmed =
    window.confirm(
      `Удалить черновик «${props.draft.title || "Без названия"}»?`
    );

  if (!confirmed) {
    return;
  }

  emit(
    "delete-draft",
    props.draft
  );
}


// ========================================================
// SOURCE
// ========================================================

const sourceTitle = computed(() => {
  const provider =
    props.draft.sourceProvider;

  const titles = {
    blueprint: "Blueprint",
    vseti: "VSETI",
    vitrina: "Vitrina",
    vdhl: "VDHL"
  };

  return titles[provider]
    || provider
    || "Источник";
});


// ========================================================
// COMPANY STATUS
// ========================================================

const companyStatusTitle = computed(() => {
  const status =
    props.draft.companyResolutionStatus;

  const titles = {
    pending: "Ожидает",
    resolved: "Найдена",
    not_found: "Не найдена",
    ambiguous: "Нужно проверить",
    failed: "Ошибка"
  };

  return titles[status]
    || status
    || "Ожидает";
});


// ========================================================
// IMAGE STATUS
// ========================================================

const imageStatusTitle = computed(() => {
  const status =
    props.draft.imageResolutionStatus;

  const titles = {
    pending: "Ожидает",
    resolved: "Найдено",
    not_found: "Не найдено",
    failed: "Ошибка"
  };

  return titles[status]
    || status
    || "Ожидает";
});


// ========================================================
// SHORT ID
// ========================================================

const shortId = computed(() => {
  const id =
    props.draft.id;

  if (!id) {
    return "";
  }

  if (id.length <= 16) {
    return id;
  }

  return `${id.slice(0, 8)}…${id.slice(-6)}`;
});


// ========================================================
// SALARY
// ========================================================

const salaryText = computed(() => {
  if (props.draft.salaryText) {
    return props.draft.salaryText;
  }

  const from =
    props.draft.salaryFrom;

  const to =
    props.draft.salaryTo;

  const currency =
    props.draft.salaryCurrency
    || "";

  if (
    from != null
    && to != null
  ) {
    return `${formatNumber(from)} – ${formatNumber(to)} ${currency}`.trim();
  }

  if (from != null) {
    return `от ${formatNumber(from)} ${currency}`.trim();
  }

  if (to != null) {
    return `до ${formatNumber(to)} ${currency}`.trim();
  }

  return null;
});


// ========================================================
// UPDATED AT
// ========================================================

const formattedUpdatedAt = computed(() => {
  const date =
    props.draft.updatedDate;

  if (!date) {
    return null;
  }

  return new Intl.DateTimeFormat(
    "ru-RU",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }
  ).format(date);
});


// ========================================================
// HELPERS
// ========================================================

function formatNumber(value) {
  return new Intl.NumberFormat(
    "ru-RU"
  ).format(value);
}


function statusClass(status) {
  return {
    "status-resolved":
      status === "resolved",

    "status-not-found":
      status === "not_found",

    "status-ambiguous":
      status === "ambiguous",

    "status-failed":
      status === "failed",

    "status-pending":
      !status
      || status === "pending"
  };
}
</script>

<style scoped>
.draft-card {
  display: grid;
  grid-template-columns: 210px minmax(0, 1fr);
  overflow: hidden;
  border: 1px solid var(--vd-border);
  border-radius: 18px;
  background: var(--vd-card);
  box-shadow: 0 5px 24px rgba(0, 0, 0, 0.04);
  transition:
    border-color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.draft-card:hover {
  transform: translateY(-1px);
  border-color: var(--vd-border-hover);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.07);
}

.draft-card-ready {
  border-color: rgba(46, 160, 93, 0.28);
}

.draft-image-wrapper {
  position: relative;
  min-height: 220px;
  background: var(--vd-image-bg);
}

.draft-image {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 220px;
  object-fit: cover;
}

.draft-image-placeholder {
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 220px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--vd-muted);
  font-size: 12px;
}

.draft-image-placeholder svg {
  width: 34px;
  height: 34px;
  opacity: 0.65;
}

.ready-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 6px 9px;
  border-radius: 999px;
  background: rgba(20, 20, 20, 0.78);
  color: white;
  font-size: 11px;
  font-weight: 600;
  backdrop-filter: blur(8px);
}

.ready-badge.ready {
  background: rgba(25, 121, 68, 0.9);
}

.draft-body {
  display: flex;
  min-width: 0;
  flex-direction: column;
  padding: 18px 20px 14px;
}

.draft-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.draft-title-block {
  min-width: 0;
}

.source-row {
  display: flex;
  margin-bottom: 8px;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.source-badge {
  padding: 4px 7px;
  border-radius: 6px;
  background: var(--vd-soft);
  color: var(--vd-muted);
  font-size: 10px;
  font-weight: 650;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.external-id {
  color: var(--vd-muted);
  font-size: 10px;
}

.draft-title {
  margin: 0;
  color: var(--vd-text);
  font-size: 18px;
  font-weight: 650;
  line-height: 1.28;
}

.company-line {
  display: flex;
  margin-top: 7px;
  align-items: baseline;
  gap: 6px;
}

.company-prefix {
  color: var(--vd-muted);
  font-size: 12px;
}

.company-name {
  color: var(--vd-text);
  font-size: 13px;
  font-weight: 600;
}

.header-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 7px;
}

.source-link,
.delete-draft-button {
  display: flex;
  width: 34px;
  height: 34px;
  padding: 0;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--vd-border);
  border-radius: 9px;
  background: transparent;
  color: var(--vd-muted);
}

.source-link {
  text-decoration: none;
  transition:
    background 0.2s ease,
    color 0.2s ease;
}

.source-link:hover {
  background: var(--vd-soft);
  color: var(--vd-text);
}

.source-link svg,
.delete-draft-button svg {
  width: 17px;
  height: 17px;
}

.delete-draft-button {
  cursor: pointer;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    transform 0.18s ease;
}

.delete-draft-button:hover {
  transform: translateY(-1px);
  border-color: rgba(208, 88, 88, 0.35);
  background: rgba(208, 88, 88, 0.08);
  color: #d05858;
}

.delete-draft-button:active {
  transform: translateY(0);
}

.meta-row {
  display: flex;
  margin-top: 14px;
  flex-wrap: wrap;
  gap: 7px;
}

.meta-item {
  padding: 5px 8px;
  border-radius: 7px;
  background: var(--vd-soft);
  color: var(--vd-secondary);
  font-size: 11px;
}

.draft-summary {
  display: -webkit-box;
  margin: 14px 0 0;
  overflow: hidden;
  color: var(--vd-secondary);
  font-size: 12px;
  line-height: 1.55;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

/* =========================================================
   CONTACTS
========================================================= */

.contacts-block {
  margin-top: 15px;
  padding: 11px 12px;
  border: 1px solid var(--vd-border);
  border-radius: 11px;
  background: color-mix(in srgb, var(--vd-soft) 45%, transparent);
}

.contacts-header {
  display: flex;
  margin-bottom: 8px;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.contacts-label {
  color: var(--vd-muted);
  font-size: 10px;
  font-weight: 600;
}

.contacts-hint {
  color: var(--vd-muted);
  font-size: 9px;
  opacity: 0.7;
}

.contacts-list {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.contact-chip {
  display: inline-flex;
  min-height: 32px;
  max-width: 100%;
  padding: 0 10px;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  border: 1px solid var(--vd-border);
  border-radius: 9px;
  background: var(--vd-card);
  color: var(--vd-secondary);
  font-size: 10px;
  font-weight: 600;
  text-decoration: none;
  transition:
    transform 0.18s ease,
    border-color 0.18s ease,
    background-color 0.18s ease,
    color 0.18s ease;
}

.contact-chip:hover {
  transform: translateY(-1px);
  border-color: var(--vd-border-hover);
  color: var(--vd-text);
}

.contact-chip svg {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}

.contact-chip span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-chip.telegram:hover {
  border-color: rgba(45, 156, 219, 0.3);
  background: rgba(45, 156, 219, 0.08);
  color: #2d9cdb;
}

.contact-chip.email:hover {
  border-color: rgba(102, 117, 239, 0.3);
  background: rgba(102, 117, 239, 0.08);
  color: #6675ef;
}

.contact-chip.apply:hover {
  border-color: rgba(36, 163, 94, 0.3);
  background: rgba(36, 163, 94, 0.08);
  color: #24a35e;
}

/* =========================================================
   RESOLUTION
========================================================= */

.resolution-grid {
  display: grid;
  margin-top: 17px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.resolution-item {
  min-width: 0;
  padding: 11px 12px;
  border: 1px solid var(--vd-border);
  border-radius: 11px;
}

.resolution-label {
  margin-bottom: 7px;
  color: var(--vd-muted);
  font-size: 10px;
  font-weight: 600;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

.status-resolved {
  color: #24945a;
}

.status-not-found {
  color: #d05858;
}

.status-ambiguous {
  color: #bc8224;
}

.status-failed {
  color: #d05858;
}

.status-pending {
  color: var(--vd-muted);
}

.resolution-info,
.resolution-error {
  margin-top: 6px;
  overflow: hidden;
  font-size: 10px;
  line-height: 1.35;
  text-overflow: ellipsis;
}

.resolution-info {
  color: var(--vd-muted);
}

.resolution-error {
  color: #bf6161;
}

.storage-path {
  white-space: nowrap;
}

/* =========================================================
   ACTIONS
========================================================= */

.draft-actions {
  display: flex;
  margin-top: 16px;
  padding-top: 14px;
  flex-wrap: wrap;
  gap: 8px;
  border-top: 1px solid var(--vd-border);
}

.draft-action-button {
  display: inline-flex;
  height: 36px;
  padding: 0 13px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  cursor: pointer;
  border: 1px solid transparent;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 600;
  transition:
    transform 0.18s ease,
    opacity 0.18s ease,
    box-shadow 0.18s ease,
    background-color 0.18s ease;
}

.draft-action-button:hover {
  transform: translateY(-1px);
}

.draft-action-button:active {
  transform: translateY(0);
}

.draft-action-button svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.draft-action-button.publish {
  background: linear-gradient(135deg, #24a35e, #218d55);
  color: #ffffff;
  box-shadow: 0 6px 14px rgba(34, 151, 87, 0.18);
}

.draft-action-button.publish:hover {
  box-shadow: 0 8px 18px rgba(34, 151, 87, 0.26);
}

.draft-action-button.company {
  background: linear-gradient(135deg, #6675ef, #765cdd);
  color: #ffffff;
  box-shadow: 0 6px 14px rgba(102, 97, 224, 0.18);
}

.draft-action-button.company:hover {
  box-shadow: 0 8px 18px rgba(102, 97, 224, 0.26);
}

.draft-action-button.image {
  border-color: var(--vd-border);
  background: var(--vd-soft);
  color: var(--vd-text);
}

.draft-action-button.image:hover {
  border-color: var(--vd-border-hover);
}

.draft-action-button:disabled {
  cursor: not-allowed;
  opacity: 0.42;
  transform: none;
  box-shadow: none;
}

.draft-action-button:disabled:hover {
  transform: none;
  box-shadow: none;
}

.draft-footer {
  display: flex;
  margin-top: auto;
  padding-top: 15px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: var(--vd-muted);
  font-size: 9px;
}

.draft-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

/* =========================================================
   RESPONSIVE
========================================================= */

@media (max-width: 760px) {
  .draft-card {
    grid-template-columns: 1fr;
  }

  .draft-image-wrapper,
  .draft-image,
  .draft-image-placeholder {
    min-height: 190px;
    max-height: 240px;
  }
}

@media (max-width: 520px) {
  .resolution-grid {
    grid-template-columns: 1fr;
  }

  .contacts-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 3px;
  }

  .contact-chip {
    max-width: 100%;
  }
}

.draft-description {
  display: -webkit-box;
  margin: 14px 0 0;
  overflow: hidden;
  color: var(--vd-secondary);
  font-size: 12px;
  line-height: 1.55;
  white-space: pre-line;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
}

.description-block {
  margin-top: 16px;
}

.description-header {
  display: flex;
  margin-bottom: 7px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.description-header > span:first-child {
  color: var(--vd-muted);
  font-size: 10px;
  font-weight: 600;
}

.description-length {
  color: var(--vd-muted);
  font-size: 9px;
}

.draft-description-input {
  width: 100%;
  min-height: 190px;
  max-height: 460px;
  padding: 13px 14px;
  resize: vertical;
  border: 1px solid var(--vd-border);
  border-radius: 11px;
  outline: none;
  background: var(--vd-soft);
  color: var(--vd-text);
  font: inherit;
  font-size: 12px;
  line-height: 1.55;
  white-space: pre-wrap;
  box-sizing: border-box;
}

.draft-description-input:focus {
  border-color: var(--vd-border-hover);
}

.description-block {
  margin-top: 16px;
}

.description-header {
  display: flex;
  margin-bottom: 7px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.description-header > span:first-child {
  color: var(--vd-muted);
  font-size: 10px;
  font-weight: 600;
}

.description-length {
  color: var(--vd-muted);
  font-size: 9px;
}

.draft-description-input {
  width: 100%;
  min-height: 220px;
  max-height: 520px;
  padding: 13px 14px;
  resize: vertical;
  box-sizing: border-box;
  border: 1px solid var(--vd-border);
  border-radius: 11px;
  outline: none;
  background: var(--vd-soft);
  color: var(--vd-text);
  font: inherit;
  font-size: 12px;
  line-height: 1.55;
}

.draft-description-input:focus {
  border-color: var(--vd-border-hover);
}

.description-actions {
  display: flex;
  margin-top: 9px;
  justify-content: flex-end;
}

.update-description-button {
  min-width: 110px;
  height: 34px;
  padding: 0 14px;
  border: 0;
  border-radius: 9px;
  background: linear-gradient(
    135deg,
    #6675ef,
    #765cdd
  );
  color: #ffffff;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.update-description-button:hover:not(:disabled) {
  transform: translateY(-1px);
}

.update-description-button:disabled {
  cursor: default;
  opacity: 0.38;
}

.draft-title-editor {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 7px;
}


.draft-title {
  min-width: 0;
}


.edit-title-button {
  display: flex;

  width: 28px;
  height: 28px;

  padding: 0;

  flex: 0 0 auto;

  align-items: center;
  justify-content: center;

  border: 0;
  border-radius: 7px;

  background: transparent;
  color: var(--vd-muted);

  cursor: pointer;

  opacity: 0;

  transition:
    opacity 0.18s ease,
    background-color 0.18s ease,
    color 0.18s ease;
}


.draft-title-editor:hover
.edit-title-button {
  opacity: 1;
}


.edit-title-button:hover {
  background: var(--vd-soft);
  color: var(--vd-text);
}


.edit-title-button svg {
  width: 14px;
  height: 14px;
}


.draft-title-edit-row {
  display: flex;

  width: min(620px, 100%);

  align-items: center;

  gap: 6px;
}


.draft-title-input {
  width: 100%;
  min-width: 0;

  height: 38px;

  padding:
    0
    11px;

  box-sizing: border-box;

  border:
    1px solid
    var(--vd-border);

  border-radius: 9px;

  outline: none;

  background:
    var(--vd-soft);

  color:
    var(--vd-text);

  font: inherit;

  font-size: 17px;
  font-weight: 650;

  line-height: 1.28;

  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease;
}


.draft-title-input:focus {
  border-color:
    #6675ef;

  box-shadow:
    0 0 0 3px
    rgba(
      102,
      117,
      239,
      0.09
    );
}


.title-save-button,
.title-cancel-button {
  display: flex;

  width: 34px;
  height: 34px;

  padding: 0;

  flex: 0 0 auto;

  align-items: center;
  justify-content: center;

  border-radius: 8px;

  cursor: pointer;

  transition:
    opacity 0.18s ease,
    transform 0.18s ease,
    background-color 0.18s ease;
}


.title-save-button {
  border: 0;

  background:
    linear-gradient(
      135deg,
      #6675ef,
      #765cdd
    );

  color: #ffffff;
}


.title-save-button:hover:not(:disabled),
.title-cancel-button:hover {
  transform:
    translateY(-1px);
}


.title-save-button:disabled {
  cursor: default;
  opacity: 0.35;
}


.title-cancel-button {
  border:
    1px solid
    var(--vd-border);

  background:
    transparent;

  color:
    var(--vd-muted);
}


.title-cancel-button:hover {
  background:
    var(--vd-soft);

  color:
    var(--vd-text);
}


.title-save-button svg,
.title-cancel-button svg {
  width: 15px;
  height: 15px;
}

.storage-folder-link,
.source-link,
.delete-draft-button {
  display: flex;
  width: 34px;
  height: 34px;
  padding: 0;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--vd-border);
  border-radius: 9px;
  background: transparent;
  color: var(--vd-muted);
}

.storage-folder-link,
.source-link {
  text-decoration: none;

  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    transform 0.18s ease;
}


.storage-folder-link:hover {
  transform: translateY(-1px);

  border-color:
    rgba(
      247,
      180,
      45,
      0.35
    );

  background:
    rgba(
      247,
      180,
      45,
      0.08
    );

  color: #d89b28;
}


.source-link:hover {
  background: var(--vd-soft);
  color: var(--vd-text);
}


.storage-folder-link svg,
.source-link svg,
.delete-draft-button svg {
  width: 17px;
  height: 17px;
}

</style>